import { KeyboardEvent, ReactNode, useState } from "react";
import { PlaceholderProps, components, createFilter } from "react-select";
import { Cross2Icon, InfoCircledIcon } from "@radix-ui/react-icons";
import { CompareBy, OrganizationSubjectAdminResponse } from "client/openapi";
import PersonPill from "components/PersonPill";
import { Select, selectComponents } from "components/Select";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/Tooltip";
import { MEASURES, Measure, UNIT } from "./data";

/** The pill Insights uses for Day, Week and Month. */
export function GroupButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`px-4 py-1 rounded-full text-sm font-medium border transition-colors ${
        active
          ? "bg-gray-800 text-white border-gray-800"
          : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
      }`}
    >
      {children}
    </button>
  );
}

export function GroupPills({
  by,
  onChange,
}: {
  by: CompareBy;
  onChange: (by: CompareBy) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {[CompareBy.DAY, CompareBy.WEEK, CompareBy.MONTH].map((value) => (
        <GroupButton
          key={value}
          active={by === value}
          onClick={() => onChange(value)}
        >
          {UNIT[value]}
        </GroupButton>
      ))}
    </div>
  );
}

/** Insights' measure pills: filled in the measure's color when shown. */
export function MeasurePills({
  shown,
  onToggle,
}: {
  shown: Measure[];
  onToggle: (measure: Measure) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {MEASURES.map((m) => {
        const on = shown.includes(m.value);
        return (
          <button
            key={m.value}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(m.value)}
            className={`px-4 py-1 rounded-full text-sm font-medium border transition-colors ${
              on
                ? "border-transparent"
                : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
            }`}
            style={
              on
                ? {
                    background: m.color,
                    color: m.value === "hours" ? "#0F2A44" : "#fff",
                  }
                : {}
            }
          >
            {m.label}
          </button>
        );
      })}
    </div>
  );
}

type SubjectOption = { value: number; label: string };

// Search finds a subject by its name only, not its id.
const byName = createFilter<SubjectOption>({
  stringify: (option) => option.label,
});

// One line at the height of the pills: what's chosen sits where the
// placeholder does, dark once something is chosen.
const Summary = (props: PlaceholderProps<SubjectOption, true>) => (
  <components.Placeholder {...props}>
    <span
      className={`block truncate ${
        props.hasValue ? "text-slate-800" : "text-gray-500"
      }`}
    >
      {props.children}
    </span>
  </components.Placeholder>
);
const COMPACT = {
  // As the app's Select: open menus sit above everything.
  container: (base: any, state: any) => ({
    ...base,
    position: "relative",
    zIndex: state.menuIsOpen ? 100000 : base.zIndex,
  }),
  menu: (base: any) => ({ ...base, zIndex: 100000, minWidth: "16rem" }),
  control: (base: any) => ({ ...base, minHeight: 30, fontSize: "14px" }),
  valueContainer: (base: any) => ({ ...base, flexWrap: "nowrap" }),
  // A long name ends in an ellipsis instead of running out of the box.
  placeholder: (base: any) => ({ ...base, minWidth: 0, overflow: "hidden" }),
  // No focus ring on the search box inside: the control shows the focus.
  input: (base: any) => ({
    ...base,
    margin: 0,
    padding: 0,
    "input:focus": { boxShadow: "none" },
  }),
};

/**
 * Subjects to show, searchable, several at once; none chosen means all. The
 * closed filter is one line: "All subjects", the subject's name, or
 * "N subjects".
 */
export function SubjectFilter({
  subjects,
  value,
  onChange,
}: {
  subjects: OrganizationSubjectAdminResponse[];
  value: number[];
  onChange: (subjectIds: number[]) => void;
}) {
  const options: SubjectOption[] = subjects.map((s) => ({
    value: s.id,
    label: s.name,
  }));
  const chosen = options.filter((o) => value.includes(o.value));
  const summary =
    chosen.length === 0
      ? "All subjects"
      : chosen.length === 1
      ? chosen[0].label
      : `${chosen.length} subjects`;
  return (
    <div className="w-52">
      <Select
        aria-label="Subjects"
        isMulti
        isClearable={chosen.length > 0}
        controlShouldRenderValue={false}
        // The chosen subjects aren't drawn, so keys mustn't remove them unseen.
        backspaceRemovesValue={false}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
            e.preventDefault();
          }
        }}
        placeholder={summary}
        components={{ ...selectComponents, Placeholder: Summary }}
        styles={COMPACT}
        options={options}
        filterOption={byName}
        value={chosen}
        onChange={(chosen: { value: number }[]) =>
          onChange((chosen ?? []).map((o) => o.value))
        }
      />
    </div>
  );
}

/** An (i) that says more: on hover, and on click or tap. */
export function InfoTip({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger>
        <button
          type="button"
          aria-label={label}
          className="text-gray-500 hover:text-gray-700"
          onClick={(e) => {
            e.preventDefault(); // keep it open: the tooltip closes on click
            setOpen(true);
          }}
        >
          <InfoCircledIcon className="h-4 w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <div className="w-64">{children}</div>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The chosen subjects under a subject filter, as pills whose × takes one
 * off, then an (i) for what the filter leaves out. None while every subject
 * is shown.
 */
export function SubjectPills({
  subjects,
  value,
  onChange,
}: {
  subjects: OrganizationSubjectAdminResponse[];
  value: number[];
  onChange: (subjectIds: number[]) => void;
}) {
  const chosen = value.flatMap((id) => subjects.filter((s) => s.id === id));
  if (!chosen.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {chosen.map((subject) => (
        <span
          key={subject.id}
          className="inline-flex min-w-0 max-w-[16rem] items-center gap-0.5"
        >
          <PersonPill label={subject.name} />
          <button
            type="button"
            className="rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label={`Remove ${subject.name}`}
            title={`Remove ${subject.name}`}
            onClick={() => onChange(value.filter((id) => id !== subject.id))}
          >
            <Cross2Icon width="10" height="10" />
          </button>
        </span>
      ))}
      <InfoTip label="About the subject filter">
        Charges that aren't tied to a subject are left out.
      </InfoTip>
    </div>
  );
}

export function StatusBlock({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center justify-center h-48 text-sm text-gray-500 gap-3">
      {children}
    </div>
  );
}
